/**
 * Générateur — Il utilise les notations et le vocabulaire fonctionnels.
 * Attendu ID : 221 | Niveau : 3ème | Domaine : Fonctions (d13, O9)
 *
 * Source doc prof (OGD FONCTIONS 3e, séances B3S1/B3S4). Plan auto-validé
 * 2026-07-16 (TODO §6) : « phrase » (égalité → phrase image/antécédent),
 * « egalite » (phrase → égalité, y compris le PIÈGE « a pour antécédent »),
 * « motjuste » (tableau : image ou antécédent ? QCM).
 */

import { randomChoice, randInt, shuffle, tex, makeQcm, makeTexteTrou } from '../../../utils';
import { frNb, tableFn, NOMS_FN } from './fonctions';

/** Un nombre écrit en toutes lettres dans une formule : virgule protégée. */
const nb = (v) => String(v).replace(',', '{,}');

const ATTENDU_ID = 221;
const L_PHRASE = 'Traduire une égalité par une phrase (image, antécédent)';
const L_EGALITE = 'Traduire une phrase par une égalité';
const L_MOTJUSTE = 'Choisir entre « image » et « antécédent »';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const valeur = () => {
    const ent = randInt(-31, 32);
    return randInt(0, 2) === 0 ? frNb(ent + randomChoice([0.5, 0.2, -0.5])) : String(ent);
};

// ═════════ « phrase » — f(a) = b → phrases ═════════

const genPhraseTrous = () => {
    const nom = randomChoice(NOMS_FN);
    let a; let b;
    do { a = valeur(); b = valeur(); } while (a === b);
    const phrase = `L'image de {{${a}}} par ${nom} est {{${b}}} ; un antécédent de {{${b}}} par ${nom} est {{${a}}}.`;
    const payload = makeTexteTrou(phrase,
        `<p>On donne l'égalité ${tex(`${nom}(${nb(a)}) = ${nb(b)}`)}.</p>`
        // « l'une avec image, l'autre avec antécédent » : les deux mots sont
        // déjà écrits dans la phrase à compléter.
        + '<p><b>Complète les deux traductions.</b></p>',
        { ...opts(L_PHRASE), mode: 'saisie' });
    payload._meta.params = { nom, a, b };
    return payload;
};

// ═════════ « egalite » — phrase → égalité (avec le piège) ═════════

const genEgaliteTrous = () => {
    const nom = randomChoice(NOMS_FN);
    let a; let b;
    do { a = valeur(); b = valeur(); } while (a === b);
    const forme = randomChoice(['image', 'antecedent']);
    let texte; let phrase;
    if (forme === 'image') {
        // « a a pour image b par f » → f(a) = b
        texte = `<p>« ${a} a pour image ${b} par ${nom}. »</p>`;
        phrase = `$${nom}\\left({{${a}}}\\right) = {{${b}}}$`;
    } else {
        // « a a pour antécédent b par f » → f(b) = a — le PIÈGE de l'inversion.
        texte = `<p>« ${a} a pour antécédent ${b} par ${nom}. »</p>`;
        phrase = `$${nom}\\left({{${b}}}\\right) = {{${a}}}$`;
    }
    const payload = makeTexteTrou(phrase,
        // « l'antécédent est DANS la parenthèse » désamorçait précisément le
        // piège de l'inversion que cette carte construit.
        `${texte}<p><b>Traduis cette phrase par une égalité.</b></p>`,
        { ...opts(L_EGALITE), mode: 'saisie' });
    payload._meta.params = { nom, a, b, forme };
    return payload;
};

// ═════════ « motjuste » — image ou antécédent ? (QCM) ═════════

const genMotJusteQcm = () => {
    const nom = randomChoice(NOMS_FN);
    // Pools DISJOINTS par construction : aucune valeur commune ligne 1/ligne 2
    // → chaque relation image/antécédent est sans ambiguïté.
    const xs = shuffle([-4, -3, -2, -1, 0, 1, 2, 3, 4]).slice(0, 5);
    const ys = shuffle([-9, -8, -7, 6, 7, 8, 9, 11]).slice(0, 5);
    const i = randInt(0, 4);
    const estImage = randInt(0, 1) === 1;
    const question = estImage
        ? `Dans la phrase « ${ys[i]} est … de ${xs[i]} par ${nom} », le mot manquant est :`
        : `Dans la phrase « ${xs[i]} est … de ${ys[i]} par ${nom} », le mot manquant est :`;
    const correct = estImage ? 'l\'image' : 'un antécédent';
    const payload = makeQcm(['l\'image', 'un antécédent', 'le coefficient'], correct,
        `<p>Voici un tableau de valeurs de la fonction ${nom} :</p>`
        + tableFn(nom, xs.map(String), ys.map(String))
        + `<p><b>${question}</b></p>`,
        opts(L_MOTJUSTE));
    payload._meta.params = { nom, xs, ys, i, estImage };
    return payload;
};

const GENERATORS = {
    phrase_texte_trou_saisie: genPhraseTrous,
    egalite_texte_trou_saisie: genEgaliteTrous,
    motjuste_qcm_unique: genMotJusteQcm,
};
const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'phrase', label: L_PHRASE, formatsList: buildFormats('phrase') },
    { id: 'egalite', label: L_EGALITE, formatsList: buildFormats('egalite') },
    { id: 'motjuste', label: L_MOTJUSTE, formatsList: buildFormats('motjuste') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
